import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckSquare, Square, FilePlus2, EyeOff, Loader2, Umbrella, Thermometer, Bandage, Clock, HelpCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const GG = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
const fmt = (iso) => `${GG[new Date(iso + "T12:00:00").getDay()]} ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const TIPI = [
  { k: "ferie", l: "Ferie", icon: Umbrella },
  { k: "malattia", l: "Malattia", icon: Thermometer },
  { k: "infortunio", l: "Infortunio", icon: Bandage },
  { k: "permesso", l: "Permesso", icon: Clock },
  { k: "altro", l: "Altro", icon: HelpCircle },
];

export function hintOf(m) {
  if (!m.programma_esiste) return { t: "Nessun programma salvato per questo giorno", c: "border-slate-300 bg-white text-slate-700" };
  if (!m.programma) return { t: "Nel programma non era assegnato a nessuna sede", c: "border-slate-300 bg-slate-100 text-slate-600" };
  if (m.programma === "Disponibili" || m.programma === "Da valutare") return { t: `Nel programma era in "${m.programma}"`, c: "border-sky-300 bg-sky-50 text-sky-800" };
  if (m.colleghi_con_ore > 0) return { t: `Previsto a ${m.programma}: ${m.colleghi_con_ore} colleghi hanno ore → probabile dimenticanza`, c: "border-red-300 bg-red-50 text-red-800" };
  return { t: `Previsto a ${m.programma} (nessun collega con ore)`, c: "border-amber-300 bg-amber-50 text-amber-800" };
}

export default function DipendenteCard({ emp, canEdit, onDone }) {
  const navigate = useNavigate();
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const all = emp.mancanti.map((m) => m.data);
  const toggle = (d) => setSel((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setSel([]);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };
  const assenza = (tipo) => {
    let note = "";
    if (tipo === "altro" || tipo === "permesso") {
      note = window.prompt("Motivazione (facoltativa):", "") ?? null;
      if (note === null) return;
    }
    run(() => api.post("/controllo-ore/assenza", { employee_id: emp.employee_id, giorni: sel, tipo, note }),
      `${sel.length} giorni registrati come ${TIPI.find((t) => t.k === tipo).l.toLowerCase()}: il Programma è aggiornato`);
  };
  const ignora = () => {
    const motivo = window.prompt("Perché ignorare queste giornate? (facoltativo)", "") ;
    if (motivo === null) return;
    run(() => api.post("/controllo-ore/ignora", { employee_id: emp.employee_id, giorni: sel, motivo }), `${sel.length} giorni ignorati`);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`controllo-emp-${emp.employee_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold" data-testid="controllo-emp-nome">{emp.nome}</span>
          <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50" data-testid="controllo-emp-count">{emp.mancanti.length} senza ore</Badge>
          <span className="text-xs text-slate-500">{emp.giorni_con_ore} {emp.giorni_con_ore === 1 ? "giorno" : "giorni"} con ore · {emp.ore_tot}h{emp.assenze_coperte ? ` · ${emp.assenze_coperte} in assenza` : ""}</span>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setSel(sel.length === all.length ? [] : all)} className="text-xs font-semibold text-[#0044FF] inline-flex items-center gap-1" data-testid="controllo-select-all">
            {sel.length === all.length ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
            {sel.length === all.length ? "Deseleziona tutte" : "Seleziona tutte"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {emp.mancanti.map((m) => {
          const h = hintOf(m);
          const on = sel.includes(m.data);
          return (
            <button key={m.data} type="button" disabled={!canEdit} title={h.t} onClick={() => toggle(m.data)}
              data-testid={`controllo-day-${emp.employee_id}-${m.data}`}
              className={`rounded-md border px-2 py-1 text-xs font-semibold transition-colors ${on ? "ring-2 ring-[#0044FF] ring-offset-1" : ""} ${h.c} ${canEdit ? "hover:brightness-95" : "cursor-default"}`}>
              {fmt(m.data)}{m.weekend ? " ⚠" : ""}
            </button>
          );
        })}
      </div>
      {canEdit && sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-100" data-testid="controllo-actions">
          <span className="text-xs text-slate-500 mr-1">{sel.length} selezionati →</span>
          {TIPI.map((t) => (
            <Button key={t.k} size="sm" variant="outline" disabled={busy} onClick={() => assenza(t.k)} data-testid={`controllo-assenza-${t.k}`} className="h-7 text-xs">
              <t.icon className="h-3.5 w-3.5 mr-1" />{t.l}
            </Button>
          ))}
          <Button size="sm" variant="outline" disabled={busy} onClick={ignora} data-testid="controllo-ignora" className="h-7 text-xs border-slate-300 text-slate-600">
            <EyeOff className="h-3.5 w-3.5 mr-1" />Ignora
          </Button>
          {sel.length === 1 && (
            <Button size="sm" disabled={busy} onClick={() => navigate(`/rapportino/new?data=${sel[0]}&dipendente=${emp.employee_id}`)} data-testid="controllo-nuovo-rapportino" className="h-7 text-xs bg-[#0044FF] hover:bg-[#0033CC]">
              <FilePlus2 className="h-3.5 w-3.5 mr-1" />Nuovo rapportino
            </Button>
          )}
          {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
      )}
    </div>
  );
}
